// Every read and write to the database goes through here.
//
// This app has no database of its own: it signs into Meal Hat's project and
// reads the hat (meals, grocery catalog, shopping list, fridge key), reads
// the fridge, and keeps its own state under `<hat>/cooking`. That node
// needs no new rules: Meal Hat's `$hat` rule grants members read and write
// of the whole hat, and Meal Hat itself subscribes per child, so a new child
// costs it nothing.
//
// REST with `?auth=<id token>`, not the realtime SDK — Movie Requests'
// choice, for the same reason: nothing here needs a live listener and the
// SDK is a hundred kilobytes of bundle for rows that change once a week.
import { getAuth, onAuthStateChanged } from 'firebase/auth';

// Against the emulators (see firebase.js) every REST call goes to the local
// database instead, namespaced the way the emulator expects.
const USE_EMULATORS = process.env.VUE_APP_FIREBASE_EMULATORS === '1';
const DATABASE_URL = USE_EMULATORS ? `http://${process.env.VUE_APP_EMULATOR_HOST || 'localhost'}:9000` : 'https://meal-hat-default-rtdb.firebaseio.com';
const NAMESPACE = USE_EMULATORS ? 'ns=meal-hat-default-rtdb' : '';

let firstAuthResult = null;

function authReady () {
  if (!firstAuthResult) {
    firstAuthResult = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(
        getAuth(),
        (user) => { unsubscribe(); resolve(user || null); },
        () => resolve(null)
      );
    });
  }
  return firstAuthResult;
}

/** The current user's ID token, or null when nobody is signed in. */
export async function authToken () {
  try {
    let user = getAuth()?.currentUser;
    if (!user) {
      await authReady();
      user = getAuth()?.currentUser;
    }
    if (!user) return null;
    return await user.getIdToken();
  } catch (error) {
    console.warn('Could not get an auth token', error);
    return null;
  }
}

export function buildUrl (path, token, query = '') {
  const clean = String(path || '').replace(/^\/+/, '');
  const url = `${DATABASE_URL}/${clean}.json`;
  const parts = [];
  if (NAMESPACE) parts.push(NAMESPACE);
  if (token) parts.push(`auth=${encodeURIComponent(token)}`);
  if (query) parts.push(query);
  return parts.length ? `${url}?${parts.join('&')}` : url;
}

async function request (path, options = {}, query = '') {
  const token = await authToken();
  const response = await fetch(buildUrl(path, token, query), options);
  if (!response.ok) {
    const error = new Error(`The database responded ${response.status}`);
    error.status = response.status;
    throw error;
  }
  const text = await response.text();
  return text && text !== 'null' ? JSON.parse(text) : null;
}

export const dbGet = (path, query) => request(path, {}, query);
export const dbPost = (path, body) => request(path, { method: 'POST', body: JSON.stringify(body) });
export const dbPut = (path, body) => request(path, { method: 'PUT', body: JSON.stringify(body) });
export const dbPatch = (path, body) => request(path, { method: 'PATCH', body: JSON.stringify(body) });
export const dbDelete = (path) => request(path, { method: 'DELETE' });
